import { type QueryRef, useBackgroundQuery, useQuery, useReadQuery } from '@apollo/client/react';
import { useMemo } from 'react';
import type { RecentBookingsQueryQuery } from '../../gql/graphql';
import { FeatureFlag } from '../../services/flags/enum';
import { getFeatureIsEnabled } from '../../services/flags/featureFlags';
import { recentBookingsQuery } from './recentBookingsQuery';

export const useRecentBookingsBackgroundQuery = (): QueryRef<RecentBookingsQueryQuery> => {
  const [queryRef] = useBackgroundQuery(recentBookingsQuery, { fetchPolicy: 'cache-first' });

  return queryRef;
};

export const useRecentBookings = () => {
  const { data, loading, error } = useQuery(recentBookingsQuery, {
    fetchPolicy: 'cache-first',
  });

  return {
    bookings: data?.viewer.bookings.recent ?? [],
    loading,
    error,
  };
};

export const useRecentGoodsTypeCodes = (queryRef: QueryRef<RecentBookingsQueryQuery>): string[] => {
  const { data } = useReadQuery(queryRef);
  const bookings = data.viewer.bookings.recent;

  return useMemo(() => {
    const codes = new Set<string>();
    for (const booking of bookings) {
      if (!booking) continue;
      for (const goods of booking.goods) {
        if (goods.goodsType?.code) {
          codes.add(goods.goodsType.code);
        }
      }
    }
    return [...codes];
  }, [bookings]);
};

export const useRecentVehicleTypeCodes = (): string[] => {
  const { bookings } = useRecentBookings();

  return useMemo(() => {
    if (!getFeatureIsEnabled(FeatureFlag.RecentRoutesAndVehicleTypes)) {
      return [];
    }
    const codes = new Set<string>();
    for (const booking of bookings) {
      if (!booking) continue;
      if (booking.vehicleType?.code) {
        codes.add(booking.vehicleType.code);
      }
    }
    return [...codes];
  }, [bookings]);
};

export const useRecentRouteCodes = (): string[] => {
  const { bookings } = useRecentBookings();

  return useMemo(() => {
    if (!getFeatureIsEnabled(FeatureFlag.RecentRoutesAndVehicleTypes)) {
      return [];
    }
    const codes = new Set<string>();
    for (const booking of bookings) {
      if (!booking) continue;
      if (booking.sailing?.route?.code) {
        codes.add(booking.sailing.route.code);
      }
    }
    return [...codes];
  }, [bookings]);
};
